import { useState, useEffect, useCallback, useRef } from "react";
import "../../../styles/pages/ouber.scss";
import { Tag, Space, Typography, Alert, Button, Input, Select, Row, Col, DatePicker } from "antd";
import { EyeOutlined, PlusCircleOutlined, SearchOutlined, SyncOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import PvTable from "../../../components/Table/PvTable";
import DataView from "../../../components/DataView/DataView";
import PvCardList from "../../../components/DataView/PvCardList";
import UserViewTripReqModal from "../../../components/Ouber/modals/user/UserViewTripReqModal";
import UserCancelConfirmModal from "../../../components/Ouber/modals/user/UserCancelConfirmModal";
import UserRequestTripModal from "../../../components/Ouber/modals/user/UserRequestTripModal";
import AdminViewTripRequestModal from "../../../components/Ouber/modals/admin/AdminViewTripRequestModal";
import AdminLinkTripModal from "../../../components/Ouber/modals/admin/AdminLinkTripModal";
import { useOuberService } from "../../../services";
import { showErrorToast, showSuccessToast } from "../../../components/Ouber/ToastNotification";

/**
 * The purpose of this page is to provide a unified interface for managing trip requests.
 * It serves both the User's "My Trip Requests" view and the Admin's management view.
 */
const UserManageTripReqPage = ({ isUserView = false }) => {
  const { dataView } = useSelector((state) => state.generalSlice);
  const { details: profile } = useSelector((state) => state.accountDetailSlice);
  const currentUserId = profile?.id;

  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isAdminViewModalVisible, setIsAdminViewModalVisible] = useState(false);
  const [isCancelConfirmModalVisible, setIsCancelConfirmModalVisible] = useState(false);
  const [isRequestModalVisible, setIsRequestModalVisible] = useState(false);
  const [isLinkModalVisible, setIsLinkModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [filterDest, setFilterDest] = useState("");
  const [filterPickup, setFilterPickup] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterDate, setFilterDate] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);



  const { service: ouberService } = useOuberService();
  const [data, setData] = useState([]);

  // Fetches the list of trip requests from the service based on active filters
  const fetchRequests = useCallback(async () => {
    try {
      const params = { 
        dropoff_location: filterDest || undefined,
        pickup_venue: filterPickup || undefined,
        status: filterStatus || undefined,
        trip_date: filterDate ? filterDate.format("YYYY-MM-DD") : undefined,
      };

      const response = await ouberService.getTripRequests(params);
      const requestsListRaw = Array.isArray(response) ? response : (response?.data || []);
      const requestsList = requestsListRaw.filter(r => r !== null && r !== undefined);

      const reqs = requestsList.map(r => ({ 
        ...r, 
        key: (r.trip_request_id || Math.random()).toString(), 
        id: (r.trip_request_id || "").toString(), 
        dest: r.dropoff_location, 
        pickup: r.pickup_venue, 
        date: r.trip_date ? r.trip_date.split("T")[0] : null, 
        trip_date: r.trip_date ? r.trip_date.split("T")[0] : null, 
        remarks: r.remarks, 
        status: r.request_status,
        name: r.external_passenger_name || (r.employee ? `${r.employee.first_name} ${r.employee.last_name}` : "N/A"),
        unit: r.unit || "N/A"
      }));
      
      // Formats the raw backend data into a consistent UI-friendly object structure
      setData(reqs);
    } catch (err) {
      showErrorToast("Fetch Error", "Failed to load trip requests.");
    }
  }, [filterDest, filterPickup, filterStatus, filterDate, ouberService]);

  const fetchRequestsRef = useRef(fetchRequests);
  useEffect(() => {
    fetchRequestsRef.current = fetchRequests;
  }, [fetchRequests]);

  // Triggers a debounced backend fetch whenever the user or admin modifies the filters
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRequestsRef.current();
    }, 500);
    return () => clearTimeout(handler);
  }, [filterDest, filterPickup, filterStatus, filterDate]);



  const filteredData = isUserView && currentUserId 
    ? data.filter(r => Number(r.employee_id) === Number(currentUserId))
    : data;

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    if (isUserView) {
      setIsViewModalVisible(true);
    } else {
      setIsAdminViewModalVisible(true);
    }
  };

  const handleEditRequest = (request) => {
    setSelectedRequest(request);
    setIsRequestModalVisible(true);
  };

  const handleCancelRequestInit = (request) => {
    setSelectedRequest(request);
    setIsViewModalVisible(false);
    setIsCancelConfirmModalVisible(true);
  };

  const handleExecuteCancel = async () => {
    if (!selectedRequest) return;
    setCancelLoading(true);
    try {
      await ouberService.updateRequestStatus(selectedRequest.id, { request_status: "Cancelled by User" });
      showSuccessToast("Cancelled", "Trip request successfully cancelled.");
      setIsCancelConfirmModalVisible(false);
      fetchRequests();
    } catch (e) {
      const errorMsg = e.response?.data?.message || "Could not cancel request.";
      showErrorToast("Error", errorMsg);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleApproveRequest = async () => {
    setIsAdminViewModalVisible(false);
    setIsLinkModalVisible(true);
  };

  const handleDeclineRequest = async (req) => {
    try {
      await ouberService.updateRequestStatus(req.id || req.trip_request_id, { request_status: "Declined by AO" });
      showSuccessToast("Success", "Trip request has been declined.");
      setIsAdminViewModalVisible(false);
      fetchRequests();
    } catch (e) {
      showErrorToast("Error", "Could not decline request.");
    }
  };


  const columns = [
    { title: "Request ID", dataIndex: "id", key: "id", align: "center" },
    {
      title: "Request Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => {
        let tagClass = "tag-pending";
        if (status === "Requested to Join") {
          tagClass = "tag-pending";
        } else if (status === "Approved by AO") {
          tagClass = "tag-approved";
        } else if (status === "Completed Trip") {
          tagClass = "tag-completed";
        } else if (status === "Declined by AO" || status === "Cancelled by AO" || status === "Cancelled by User") {
          tagClass = "tag-cancelled";
        }
        
        return (
          <Tag
            className={tagClass}
            style={{
              borderRadius: "12px",
              minWidth: "80px",
              textAlign: "center",
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    ...(!isUserView ? [
      { 
        title: "Requestor Name", 
        dataIndex: "name", 
        key: "name", 
        align: "center" 
      },
      {
        title: "Unit",
        dataIndex: "unit",
        key: "unit",
        align: "center",
      }
    ] : []),
    { title: "Trip Date", dataIndex: "date", key: "date", align: "center" },
    {
      title: "Pickup Venue",
      dataIndex: "pickup",
      key: "pickup",
      align: "center",
    },
    { 
      title: "Destination", 
      dataIndex: "dest", 
      key: "dest", 
      align: "center",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <EyeOutlined 
            style={{ fontSize: "18px", cursor: "pointer", color: "#1890ff" }} 
            onClick={() => handleViewRequest(record)}
          />
        </Space>
      ),
    },
    { title: "Remarks", dataIndex: "remarks", key: "remarks", align: "center" },
  ];

  const visibleColumns = columns; 

  return (
    <div>
      <Space className="ouber-page-header">
        <Typography.Title level={3} className="ouber-page-title">
          {isUserView ? "My Trip Requests" : "Manage Trip Requests"}
        </Typography.Title>
        {isUserView && (
          <Button
            type="primary"
            icon={<PlusCircleOutlined />}
            className="ouber-btn-forest"
            onClick={() => {
              setSelectedRequest(null);
              setIsRequestModalVisible(true);
            }}
          >
            Request Trip
          </Button>
        )}
      </Space>

      <Alert
        className="ouber-alert"
        message={<span className="ouber-alert-title">My Trip Requests</span>}
        description={
          <span className="ouber-alert-desc">
            View and manage all trip requests you have made. You can cancel at anytime. 
          </span>
        }
        type="info"
        showIcon
      />

      <div className="ouber-filter-container">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Pickup Venue" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterPickup}
              onChange={(e) => setFilterPickup(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Destination" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterDest}
              onChange={(e) => setFilterDest(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <DatePicker 
              placeholder="Filter by Date"
              style={{ width: "100%" }}
              format="MM-DD-YYYY"
              value={filterDate}
              onChange={(date) => setFilterDate(date)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Status"
              style={{ width: "100%" }}
              allowClear
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              options={[
                { value: "Completed Trip", label: "Completed Trip" },
                { value: "Approved by AO", label: "Approved by AO" },
                { value: "Requested to Join", label: "Requested to Join" },
                { value: "Declined by AO", label: "Declined by AO" },
                { value: "Cancelled by AO", label: "Cancelled by AO" },
                { value: "Cancelled by User", label: "Cancelled by User" }
              ]}
            />
          </Col>
        </Row>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "12px" }}>
        <DataView />
        <Button 
          icon={<SyncOutlined />} 
          onClick={fetchRequests} 
          className="ouber-btn-primary"
          style={{ marginBottom: 0 }}
        >
          Sync Data
        </Button>
      </div>

      {dataView === "table" ? (
        <PvTable
          data={filteredData}
          columns={visibleColumns}
          loading={false}
          onRowClick={() => {}}
        />
      ) : (
        <PvCardList
          data={filteredData}
          columns={visibleColumns}
          onRowClick={() => {}}
          loading={false}
          maxActiveRow={filteredData.length}
        />
      )}

      {isUserView ? (
        <UserViewTripReqModal 
          isOpen={isViewModalVisible}
          onClose={() => setIsViewModalVisible(false)}
          requestData={selectedRequest}
          onCancelRequest={handleCancelRequestInit}
        />
      ) : (
        <AdminViewTripRequestModal 
          isOpen={isAdminViewModalVisible}
          onClose={() => setIsAdminViewModalVisible(false)}
          requestData={selectedRequest}
          onCreateLinkedTrip={handleApproveRequest}
          onEdit={handleEditRequest}
          onDecline={handleDeclineRequest}
        />
      )}

      <AdminLinkTripModal 
        open={isLinkModalVisible}
        onCancel={() => setIsLinkModalVisible(false)}
        requestData={selectedRequest}
        onSave={async (newTrip) => {
          try {
            await ouberService.createTrip(newTrip);
            showSuccessToast("Success", "Trip successfully created and linked.");
            setIsLinkModalVisible(false);
            fetchRequests();
          } catch(e) {
            showErrorToast("Error", "Could not create linked trip.");
          }
        }}
      />

      <UserCancelConfirmModal 
        isOpen={isCancelConfirmModalVisible}
        onClose={() => setIsCancelConfirmModalVisible(false)}
        onConfirm={handleExecuteCancel}
        loading={cancelLoading}
        requestData={selectedRequest}
      />

      <UserRequestTripModal 
        isOpen={isRequestModalVisible}
        onClose={() => setIsRequestModalVisible(false)}
        initialValues={selectedRequest}
        onSave={async (values) => {
          try {
            if (selectedRequest) {
              await ouberService.updateRequestStatus(selectedRequest.id, values);
              showSuccessToast("Success", "Trip request successfully updated.");
            } else {
              await ouberService.createTripRequest(values);
              showSuccessToast("Success", "Trip request successfully created.");
            }
            setIsRequestModalVisible(false);
            fetchRequests();
          } catch (e) {
            const errorMsg = e.response?.data?.message || "Could not save trip request.";
            showErrorToast("Error", errorMsg);
          }
        }}
      />
    </div>
  );
};

export default UserManageTripReqPage;
